import { projectTimezone } from '@dashboard/utils/projectTimezone'
import api from '@dashboard/services/axiosApi'
import type { TeamPid } from '@dashboard/types/config'
import type { MetricCoverage } from '@dashboard/modules/segment/services/questionReportingService'

export interface NodeMetrics {
  nodeId: string
  nodeType: string
  visitorCount: number | null
  avgDurationMs: number
  splitDistribution?: Record<string, number>
  answerDistribution?: Record<string, number>
  branchDistribution?: Record<string, number>
  offerDistribution?: Record<string, number>
  bucketDistribution?: Record<string, number>
  conversionCount?: number | null
  conversionRate?: number | null
  coverage?: MetricCoverage
  /** Null for an ask node whose `coverage` is unavailable. */
  droppedCount?: number | null
}

export interface FlowMetrics {
  totalExecutions: number
  completedRuns: number
  unfinishedRuns: number
  uniqueVisitors: number
  identifiedVisitors: number
  avgDurationMs: number
  completionBreakdown: Record<string, number>
  funnel: FlowFunnel | null
  /** Server-ranked Key insights and Top drop-off points; null for team-wide requests. */
  insights: FlowInsights | null
  byFlow: {
    flowId: string
    executions: number
    uniqueVisitors: number
    completionRate: number
  }[]
}

export interface FlowDroppedStep {
  nodeId: string
  nodeType: string
  droppedCount: number
}

export interface FlowInsights {
  topDropOffs: FlowDroppedStep[]
  highestDropOffQuestion: {
    nodeId: string
    droppedCount: number
    visitorCount: number
    dropOffRate: number
  } | null
  highestAbandonmentOffer: {
    nodeId: string
    conversions: number
    visitorCount: number
    abandonmentRate: number
  } | null
  slowestQuestion: { nodeId: string; avgDurationMs: number } | null
}

export interface FlowFunnel {
  views: number
  starts: number
  submissions: number
  completionRate: number | null
  avgTimeToCompleteMs: number | null
}

export interface PathFlow {
  from: string
  to: string
  count: number
}

export interface FlowPathMetrics {
  paths: PathFlow[]
  runsAnalyzed: number
  analyzedFrom: string | null
}

export interface FlowExecutionMetricsResponse {
  flowMetrics: FlowMetrics
  nodeMetrics: Record<string, NodeMetrics>
}

export const fetchFlowExecutionMetrics = async (
  teamPid: TeamPid,
  params: {
    flowId?: string
    start: Date
    end: Date
    timezone?: string
  }
): Promise<FlowExecutionMetricsResponse> => {
  const { data } = await api.get<FlowExecutionMetricsResponse>(`/api/${teamPid}/metrics/flowExecutionMetrics`, {
    params: {
      flowId: params.flowId,
      start: params.start.toISOString(),
      end: params.end.toISOString(),
      timezone: params.timezone || projectTimezone()
    }
  })

  return data
}

export const fetchFlowPathMetrics = async (
  teamPid: TeamPid,
  params: {
    flowId: string
    start: Date
    end: Date
  }
): Promise<FlowPathMetrics> => {
  const { data } = await api.get<FlowPathMetrics>(`/api/${teamPid}/metrics/flowPathMetrics`, {
    params: {
      flowId: params.flowId,
      start: params.start.toISOString(),
      end: params.end.toISOString()
    }
  })

  return data
}

export interface BatchFlowExecutionMetricsResponse {
  flows: Record<string, FlowExecutionMetricsResponse>
}

export const fetchBatchFlowExecutionMetrics = async (
  teamPid: TeamPid,
  params: {
    flowIds: string[]
    start: Date
    end: Date
    timezone?: string
  }
): Promise<BatchFlowExecutionMetricsResponse> => {
  const { data } = await api.get<BatchFlowExecutionMetricsResponse>(`/api/${teamPid}/metrics/flowExecutionMetrics`, {
    params: {
      flowIds: params.flowIds,
      start: params.start.toISOString(),
      end: params.end.toISOString(),
      timezone: params.timezone || projectTimezone()
    }
  })

  return data
}

export interface FlowSummaryMetrics {
  flowId: string
  executions: number
  uniqueVisitors: number
  identifiedVisitors: number
  completionRate: number
  avgDurationMs: number
}

export interface FlowSummaryResponse {
  flows: Record<string, FlowSummaryMetrics>
}

export const fetchFlowSummaryMetrics = async (
  teamPid: TeamPid,
  params: {
    flowIds: string[]
    start: Date
    end: Date
    timezone?: string
  }
): Promise<FlowSummaryResponse> => {
  // Use POST if we have many flow IDs to avoid URL length limits
  // Typically URLs should stay under 2000 characters
  const usePost = params.flowIds.length > 20

  const requestData = {
    flowIds: params.flowIds,
    start: params.start.toISOString(),
    end: params.end.toISOString(),
    timezone: params.timezone || projectTimezone()
  }

  const { data } = usePost
    ? await api.post<FlowSummaryResponse>(`/api/${teamPid}/metrics/flowSummaryMetrics`, requestData)
    : await api.get<FlowSummaryResponse>(`/api/${teamPid}/metrics/flowSummaryMetrics`, { params: requestData })

  return data
}

export const deleteFlowExecutions = async (
  teamPid: TeamPid,
  flowId?: string
): Promise<{ success: boolean; deleted: number; flowId?: string }> => {
  const response = await api.delete(`/api/${teamPid}/metrics/flowExecutions`, {
    data: { flowId }
  })
  return response.data
}

// --- Flow Engagement Types & API ---

export interface FlowEngagementNode {
  nodeId: string
  nodeType: string
  timestamp?: string
  durationFromPrevious?: number
  offerId?: string
  segmentGroupId?: string
  action?: string
  branch?: string
  splitResult?: string
  url?: string
  conversionType?: string
  selectedSegmentId?: string | string[]
  freeformInput?: string
  otherText?: string
  inputValue?: string | number
  inputType?: string
  questionId?: string
  status?: 'identified' | 'already_known' | 'failed' | 'no_consent'
  parts?: Record<string, string>
}

export type FlowEngagementOutcome = 'completed' | 'abandoned' | 'closed'

export interface FlowEngagementAnswerFilter {
  segmentId: string
  questionId?: string
  segmentGroupId?: string
  nodeId?: string
}

export interface FlowEngagementSummary {
  entered: number
  answered: number
}

export interface FlowEngagement {
  id: string
  visitorId: string | null
  timestamp: string | null
  startTime: string | null
  email: string | null
  outcome: FlowEngagementOutcome
  engaged: boolean
  hasDisplayMarkers: boolean
  stopNode: FlowEngagementNode | null
  exitNode: FlowEngagementNode | null
  identified: boolean
  nodes: FlowEngagementNode[]
  completionStatus: string
  durationMs: number
  pageUrl: string | null
  deviceType: string | null
  country: string | null
}

export interface FlowEngagementsResponse {
  engagements: FlowEngagement[]
  total: number
  totalAll: number
  totalEngaged: number
  summary: FlowEngagementSummary | null
  searchAfter: string | null
}

export const fetchFlowEngagements = async (
  teamPid: string,
  params: {
    flowId: string
    start: Date
    end: Date
    email?: string
    search?: string
    outcome?: FlowEngagementOutcome | 'all'
    answerFilters?: FlowEngagementAnswerFilter[]
    limit?: number
    searchAfter?: string
    timezone?: string
  }
): Promise<FlowEngagementsResponse> => {
  const { data } = await api.get<FlowEngagementsResponse>(`/api/${teamPid}/metrics/flowEngagements`, {
    params: {
      flowId: params.flowId,
      start: params.start.toISOString(),
      end: params.end.toISOString(),
      email: params.email || undefined,
      search: params.search || undefined,
      outcome: params.outcome || undefined,
      answerFilters: params.answerFilters,
      limit: params.limit || 50,
      searchAfter: params.searchAfter || undefined,
      timezone: params.timezone || projectTimezone()
    }
  })
  return data
}
